import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { Dispatch } from 'redux';
import {
  Box,
  Stack,
  Button,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import confirm from '../../../../../../components/ConfirmDialogModal';
import ElementWithPermissions from '../../../../../../components/ElementWithPermissions';
import ConditionalTooltip from '../../../../../../components/ConditionalTooltip';
import useIntl from '../../../../../../hooks/useIntl';

import { editLease } from '../../../../actions';
import type { Action } from '../../../../../App/types';

import messages from './messages';
import { useGateTooltip } from './hooks';

type BlueMoonMethodButtonsProps = {
  actions: Object,
  allAffordableTabsApproved: boolean,
  canComplete: boolean,
  difference: number,
  hasNonFR?: boolean,
  isEditLeaseMode?: boolean,
  isResident: boolean,
  leaseId: string,
  onGenerateInBlueMoon?: Function,
  profileId: string,
  prospectId?: string,
  providerIsUsable?: boolean,
  transferCertBlocked?: boolean,
  transferCertTooltip?: string,
  unapprovedAffordablePrograms?: Array<string>,
  moveInCertBlocked?: boolean,
  moveInCertTooltip?: string,
  setIsEditLeaseMode?: Function,
};

export const BlueMoonMethodButtons = ({
  actions,
  allAffordableTabsApproved,
  canComplete,
  difference,
  hasNonFR = false,
  isEditLeaseMode = false,
  isResident,
  leaseId,
  onGenerateInBlueMoon,
  profileId,
  prospectId,
  providerIsUsable = true,
  transferCertBlocked = false,
  transferCertTooltip = '',
  unapprovedAffordablePrograms = [],
  moveInCertBlocked = false,
  moveInCertTooltip = '',
  setIsEditLeaseMode,
}: BlueMoonMethodButtonsProps) => {
  const intl = useIntl();

  const confirmEditDialog = () => {
    confirm(intl.formatMessage(messages.confirmEditLease)).then(() => {
      actions.editLease(
        leaseId,
        profileId,
        isResident,
        prospectId,
        undefined,
        undefined,
        setIsEditLeaseMode,
      );
    });
  };

  const { automateMonthlyLeaseTransactionsMvp: automateMLTsFlag } = useFlags();
  const notReconciled = automateMLTsFlag ? false : difference !== 0;

  const { certGateTooltip, showCertGateTooltip } = useGateTooltip({
    intl,
    allAffordableTabsApproved,
    unapprovedAffordablePrograms,
    transferCertBlocked,
    transferCertTooltip,
    moveInCertBlocked,
    moveInCertTooltip,
  });
  // An unusable Blue Moon connection takes the tooltip ahead of the cert gates.
  // A missing handler comes last. ENG-33905 supplies the handler.
  let generateTooltip = certGateTooltip;
  if (!providerIsUsable) {
    generateTooltip = intl.formatMessage(messages.blueMoonUnavailable);
  } else if (!showCertGateTooltip && !onGenerateInBlueMoon) {
    generateTooltip = intl.formatMessage(messages.blueMoonGeneratePending);
  }
  const showGenerateTooltip =
    !providerIsUsable || showCertGateTooltip || !onGenerateInBlueMoon;

  return (
    <>
      <Stack spacing={1} direction={'row'} justifyContent={'center'}>
        {/* The scopes the cancel route accepts. */}
        <ElementWithPermissions scope={['lease-create', 'lease-cancel']}>
          <Button
            sx={{ flex: 1, maxWidth: '150px' }}
            data-testid={'editLeaseButton'}
            variant={'primarySubtle'}
            onClick={confirmEditDialog}
            disabled={isEditLeaseMode}
          >
            {isEditLeaseMode ? (
              <Spinner small />
            ) : (
              intl.formatMessage(messages.edit)
            )}
          </Button>
        </ElementWithPermissions>
        {!isEditLeaseMode && (
          <>
            <ConditionalTooltip
              containerSX={{ display: 'flex', flex: 1, maxWidth: '150px' }}
              show={showGenerateTooltip}
              title={generateTooltip}
              placement={'top'}
              variant={'light'}
            >
              {/* span wrapper: a disabled MUI button swallows pointer events. Carries the
                  flex sizing itself since it — not the Button — is now the Stack's flex item.
                  ElementWithPermissions sits inside so the Button is its direct cloned child
                  and still receives the permission-driven `disabled`. */}
              <ElementWithPermissions scope={['lease-create']}>
                <Button
                  sx={{ flex: 1, maxWidth: '150px' }}
                  data-testid={'generateInBlueMoonButton'}
                  variant={'primary'}
                  onClick={onGenerateInBlueMoon}
                  disabled={
                    !providerIsUsable ||
                    !canComplete ||
                    hasNonFR ||
                    !onGenerateInBlueMoon ||
                    !allAffordableTabsApproved ||
                    notReconciled ||
                    moveInCertBlocked ||
                    transferCertBlocked
                  }
                >
                  {intl.formatMessage(messages.generateInBlueMoon)}
                </Button>
              </ElementWithPermissions>
            </ConditionalTooltip>
            <ConditionalTooltip
              containerSX={{ display: 'flex', flex: 1, maxWidth: '150px' }}
              show
              title={intl.formatMessage(messages.blueMoonDownloadPending)}
              placement={'top'}
              variant={'light'}
            >
              {/* No packet field exists yet, so Download Lease is always disabled. */}
              <ElementWithPermissions scope={['lease-create']}>
                <Button
                  sx={{ flex: 1, maxWidth: '150px' }}
                  data-testid={'downloadLeaseButton'}
                  variant={'primary'}
                  disabled
                >
                  {intl.formatMessage(messages.downloadLease)}
                </Button>
              </ElementWithPermissions>
            </ConditionalTooltip>
          </>
        )}
      </Stack>
      {notReconciled && (
        <div className="text-center" style={{ marginRight: '3%' }}>
          {intl.formatMessage(messages.notReconciled)}
        </div>
      )}
      <Box marginTop={1}>
        <div className="text-center" style={{ marginRight: '3%' }}>
          {intl.formatMessage(messages.blueMoonGenerateHelper)}
        </div>
      </Box>
    </>
  );
};

export function mapDispatchToProps(dispatch: Dispatch<Action<Object>>): Object {
  const actions = bindActionCreators({ editLease }, dispatch);
  return { actions };
}

export default connect(null, mapDispatchToProps)(BlueMoonMethodButtons);
